import { AgGridReact } from "ag-grid-react";
import { Fragment, useContext, useRef, useState } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { BlacklistExportTypes } from "./Types";
import ExportService from "./ExportService";
import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import SectionHeader from "../../../../util/component/SectionHeader";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { saveFileFromResponse } from "../../component/utils/Utils";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import { ContextFinesSecurity } from "../../context/FSContext";
import AgGridFSTable from "../../common/AgGridFSTable";
import { nameof } from "../../common/ObjectUtils";

const BlacklistExport = () => {
  const { t } = useTranslation(["finessecurity"]);
  const { setLoading } = useContext(ContextFinesSecurity);
  const [refreshPage, setRefreshPage] = useState<boolean>(false);

  const [datasourceAcriss, totalElementsAcriss, updateDatasourceAcriss] =
    useAgGridCustomDatasource(ExportService.get.asyncExport, [], [refreshPage]);

  const blacklistExportColumns: ColDef[] = [
    {
      field: nameof<BlacklistExportTypes.TableData>("filename"),
      headerName: t("blacklistExport.filename"),
      flex: 1,
      minWidth: 150,
    },
    {
      field: nameof<BlacklistExportTypes.TableData>("createDatetime"),
      headerName: t("blacklistExport.createDatetime"),
      flex: 1,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) =>
        dateFormat(params.data?.createDatetime),
    },
    {
      field: nameof<BlacklistExportTypes.TableData>("expirationDatetime"),
      headerName: t("blacklistExport.expirationDatetime"),
      flex: 1,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) =>
        dateFormat(params.data?.expirationDatetime),
    },
    {
      field: nameof<BlacklistExportTypes.TableData>("status"),
      headerName: t("blacklistExport.status"),
      flex: 1,
      minWidth: 100,
      cellRenderer: (params: ICellRendererParams) =>
        statusShow(params.data?.status),
    },
    {
      field: "",
      maxWidth: 100,
      minWidth: 100,
      pinned: "right",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Mui.Tooltip title={t("tooltipTitle.download")}>
            <Mui.Button
              variant="text"
              size="icon"
              disabled={params.data?.status === "PROCESSED" ? false : true}
              onClick={() => downloadExcel(params.data?.id)}
            >
              <MuiIcons.Download />
            </Mui.Button>
          </Mui.Tooltip>
        );
      },
    },
  ];

  const statusShow = (status: string) => {
    switch (status) {
      case "PROCESSED":
        return (
          <Mui.Chip
            label={t("blacklistExport.processed")}
            variant="filled"
            color="success"
          />
        );
      case "PENDING":
        return (
          <Mui.Chip
            label={t("blacklistExport.pending")}
            variant="filled"
            color="warning"
          />
        );
      case "ERROR":
        return (
          <Mui.Chip
            label={t("blacklistExport.error")}
            variant="filled"
            color="error"
          />
        );
      default:
        return <Mui.Chip label={status} variant="filled" color="info" />;
    }
  };

  const dateFormat = (date: string) => {
    if (date)
      return <Mui.Stack>{dayjs(date).format("DD/MM/YYYY-HH:mm")}</Mui.Stack>;
  };

  const downloadExcel = (id: number) => {
    if (id) {
      setLoading(true);
      ExportService.get.asyncExportById(id).then((response) => {
        saveFileFromResponse(response);
        setLoading(false);
      });
    }
  };

  return (
    <Fragment>
      <SectionHeader title={t("blacklistExport.title")} />

      <Mui.Box p={3}>
        <Mui.Card>
          <Mui.Box sx={{ justifyContent: "flex-end", display: "flex", mr: 2 }}>
            <Mui.Tooltip title={t("blacklistExport.refreshTable")}>
              <Mui.Button
                variant="text"
                size="icon"
                color="accent"
                onClick={() => setRefreshPage(!refreshPage)}
              >
                <MuiIcons.Cached sx={{ fontSize: "22px" }} />
              </Mui.Button>
            </Mui.Tooltip>
          </Mui.Box>
        </Mui.Card>
        <AgGridFSTable
          columnDefs={blacklistExportColumns}
          serverSideDatasource={datasourceAcriss}
          serverSideInfiniteScroll={true}
          cacheBlockSize={10}
          rowModelType="serverSide"
          totalElements={totalElementsAcriss}
          emptyState={{
            title:t("emptyStateGrid.title"),
            content:t("emptyStateGrid.content")
          }}
        />
      </Mui.Box>
    </Fragment>
  );
};

export default BlacklistExport;
